/**
 * i18n types — shared between Provider, dictionaries, and consumers.
 *
 * Design goals (ADR-0166):
 *   1. Zero new third-party deps. No react-i18next (~40 KB gz), no
 *      @lingui (~30 KB gz). Hand-rolled hook + Context.
 *   2. Strongly typed — message keys are a string-literal union; the
 *      `t(key)` call is checked at typecheck time so missing/typo'd
 *      keys are caught.
 *   3. Lightweight runtime — pure function lookups, no parsing,
 *      no ICU MessageFormat. Plural / variable substitution is a
 *      single-template-string interpolation handled by `t()` itself.
 *
 * If/when we grow past ~150 messages, swap this module out for
 * react-i18next without touching consumer call sites — `t()` keeps
 * its signature.
 */

/**
 * Locale codes used in URLs (`?lang=zh`), localStorage, and
 * dictionary file names. Subset chosen by the user's brand ask
 * (parity with jubit.ai/home):
 *   - en : English (default; source-of-truth)
 *   - zh : Chinese Simplified
 *   - ja : Japanese
 *   - ko : Korean
 *   - th : Thai
 *   - nl : Dutch
 *   - es : Spanish
 *   - fr : French
 */
export type Locale = 'en' | 'zh' | 'zh-TW' | 'ja' | 'ko' | 'th' | 'nl' | 'es' | 'fr';

/** Canonical order — drives the LanguageSwitcher's option list. */
export const SUPPORTED_LOCALES: ReadonlyArray<Locale> = Object.freeze([
  'en',
  'zh',
  'zh-TW',
  'ja',
  'ko',
  'th',
  'nl',
  'es',
  'fr',
]);

/**
 * Locale display metadata used by the LanguageSwitcher. `nativeName`
 * is intentionally written in the locale's own script — that's the
 * accessible convention (a Chinese user expects "中文", not "Chinese").
 */
export interface LocaleMeta {
  readonly code: Locale;
  readonly nativeName: string;
  readonly englishName: string;
  /** Single-glyph or short ISO label for compact UI; e.g. flag-emoji
   *  surrogate avoidance — we use locale codes instead of flags
   *  because flags ≠ languages (Switzerland, Belgium, etc.). */
  readonly shortLabel: string;
}

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  en: { code: 'en', nativeName: 'English', englishName: 'English', shortLabel: 'EN' },
  zh: { code: 'zh', nativeName: '简体中文', englishName: 'Chinese (Simplified)', shortLabel: '简' },
  'zh-TW': { code: 'zh-TW', nativeName: '繁體中文', englishName: 'Chinese (Traditional)', shortLabel: '繁' },
  ja: { code: 'ja', nativeName: '日本語', englishName: 'Japanese', shortLabel: '日' },
  ko: { code: 'ko', nativeName: '한국어', englishName: 'Korean', shortLabel: '한' },
  th: { code: 'th', nativeName: 'ไทย', englishName: 'Thai', shortLabel: 'ไทย' },
  nl: { code: 'nl', nativeName: 'Nederlands', englishName: 'Dutch', shortLabel: 'NL' },
  es: { code: 'es', nativeName: 'Español', englishName: 'Spanish', shortLabel: 'ES' },
  fr: { code: 'fr', nativeName: 'Français', englishName: 'French', shortLabel: 'FR' },
};

/**
 * Message keys for /home (ADR-0166, v1 scope). Adding a key here
 * forces every dictionary to satisfy the `Messages` Record type,
 * so missing translations fail typecheck instead of falling back
 * silently to English at runtime.
 *
 * Keep names section-prefixed (`hero.title`, `marquee.h2`, …) so the
 * dictionary structure stays scan-able as the count grows.
 */
export type MessageKey =
  // Hero band
  | 'hero.title'
  | 'hero.tagline.prefix'   // "One little buddy who shows up in your favourite anime style. Render across 39+ style families. Today, Jub is "
  | 'hero.tagline.suffix'   // "." — used when interpolated variant follows
  | 'hero.cta.openApp'
  | 'hero.cta.browseStyles'
  | 'hero.cta.walkPlanet'
  | 'hero.cta.driveHongKong'
  | 'hero.planetAsk.title'
  | 'hero.planetAsk.multi'
  | 'hero.planetAsk.gps'
  | 'hero.planetAsk.time'
  | 'hero.planetAsk.ink'
  | 'hero.planetAsk.go'
  | 'hero.planetAsk.soon'   // "{s}" seconds until the plain /planet door
  | 'hero.planetAsk.ready'
  | 'hero.planetAsk.locating'
  | 'hero.badge.today'      // "Today's Jub"
  // 39-variant marquee
  | 'marquee.h2'
  | 'marquee.intro'
  // Three surfaces
  | 'surfaces.h2'
  | 'surfaces.make.verb'
  | 'surfaces.make.title'
  | 'surfaces.make.copy'
  | 'surfaces.make.cta'
  | 'surfaces.meet.verb'
  | 'surfaces.meet.title'
  | 'surfaces.meet.copy'
  | 'surfaces.meet.cta'
  | 'surfaces.battle.verb'
  | 'surfaces.battle.title'
  | 'surfaces.battle.copy'
  | 'surfaces.battle.cta'
  // Apps mini-grid
  | 'apps.h2'
  | 'apps.intro'
  // Per-app taglines (ADR-0168) — looked up via
  // `apps.tile.${id}.tagline` from JUBUDDY_APPS at render time.
  // App names + badges stay in their canonical brand form (English)
  // because they're product identifiers; only the prose tagline
  // translates.
  | 'apps.tile.slop-hongkong.tagline'
  | 'apps.tile.create.tagline'
  | 'apps.tile.imagine.tagline'
  | 'apps.tile.chatlab.tagline'
  | 'apps.tile.studio.tagline'
  | 'apps.tile.pictorial-book.tagline'
  | 'apps.tile.creature-stages.tagline'
  | 'apps.tile.tcg-card-lab.tagline'
  | 'apps.tile.meshy-ref-shelf.tagline'
  | 'apps.tile.workshop.tagline'
  // ── Surface matrix + Brand discipline removed (ADR-0167) ──
  //   Those were internal documentation, not marketing copy.
  //   The reference lives in
  //   docs/patterns/jubuddy-brand-story-and-mascot-variants.md.
  // Family band — core
  | 'family.h2'
  | 'family.body.before'   // "jubuddy is the creative-play sibling to "
  | 'family.body.after'    // "'s creative-tools-suite. Same auth, same credits, different brand role: jubuddy is where you play; jubit.ai is where you work."
  | 'family.cta'
  // Family band — Jubit origin story (ADR-0167)
  | 'family.origin.h3'           // small caps section header "JUBIT, EXPLAINED"
  | 'family.origin.formula'      // "Jubit = 朱比特 (Jupiter) + Judgement Bit" — formula stays brand-stable
  | 'family.origin.body'         // "We value the finest humanity and collaborate with AI minds for a better world."
  | 'family.origin.orbit'        // "As Jupiter's 95 moons orbit in harmony, we bring 95+ AI models from 12+ providers into one unified orbit."
  | 'family.origin.chatlab.cta'  // "Explore ChatLab →"
  // Etymology section (ADR-0167) — "the name behind the buddy"
  | 'etymology.h2'
  | 'etymology.intro'
  | 'etymology.ju.label'   // "Ju" — brand-stable, but used as a key for symmetry
  | 'etymology.ju.unit'    // "单位：聚" — CJK literal, stays constant across locales (it IS the wordplay)
  | 'etymology.ju.gloss'   // localized explanation: "Magic. Charm. The force that pulls a story together."
  | 'etymology.buddy.label'      // "Buddy"
  | 'etymology.buddy.gloss'      // localized explanation: "Companionship. The friend who shows up."
  | 'etymology.combined.label'   // "Jubuddy = 聚一 (Jub It)" — formula stays brand-stable
  | 'etymology.combined.gloss'   // localized: "The spell that gathers your gang."
  | 'etymology.tagline'          // "Make. Meet. Battle." — localized
  // Showcase section (ADR-0167) — gallery of /jub-generated examples
  | 'showcase.h2'
  | 'showcase.intro'
  | 'showcase.cta'
  // Footer
  | 'footer.line1'         // "jubuddy · part of the Jubit AI family"
  | 'footer.line2'         // "39 styles · 12 heroes · 30 maps"
  | 'footer.privacy'
  | 'footer.terms'
  // Language switcher
  | 'lang.switcher.label' // aria-label on the <select>
  // ── gghere.com homepage (src/worlds/) ─────────────────────────────
  // Numbers ({cityCount}, {planetTotal}, …) are INTERPOLATED via t()'s
  // {var} substitution — the values keep deriving from WORLD_CARDS /
  // groundTruth.ts at the call site, never typed into a dictionary.
  // Header nav
  | 'gghere.nav.aria'          // aria-label on the header <nav>
  | 'gghere.nav.cities'
  | 'gghere.nav.data'
  | 'gghere.nav.honestly'
  | 'gghere.nav.play'
  // Launch Bay hero
  | 'gghere.hero.kicker'
  | 'gghere.hero.headline.line1'  // "We turned {cityCount} real cities"
  | 'gghere.hero.headline.line2'  // "into planets you can walk around."
  | 'gghere.hero.subhead'         // {planetTotal} + {continents}
  | 'gghere.hero.cta.walk'        // "Walk {place} →"
  | 'gghere.hero.cta.drop'
  | 'gghere.hero.micro'           // shared with the FAQ closing line
  // The Hong-Kong-only assistant band — {scope} is ASSISTANT_SCOPE
  | 'gghere.hk.h2'
  | 'gghere.hk.body'
  | 'gghere.hk.scopeNote'         // {scope} + {otherCount}
  | 'gghere.hk.cta'               // "Try it on {place} →"
  // City roster (the picker)
  | 'gghere.roster.aria'
  | 'gghere.roster.label'
  | 'gghere.roster.placeholder'
  | 'gghere.roster.filter.aria'
  | 'gghere.roster.filter.all'
  | 'gghere.roster.count.one'     // "{count} city"
  | 'gghere.roster.count.other'   // "{count} cities"
  | 'gghere.roster.empty'         // "No city matches “{query}”."
  | 'gghere.roster.empty.clear'
  // Number wall — "Counted, not claimed."
  | 'gghere.numbers.h2'
  | 'gghere.numbers.lead'
  | 'gghere.numbers.tile.cities'
  | 'gghere.numbers.tile.planets'
  | 'gghere.numbers.tile.continents'
  | 'gghere.numbers.measuredTag'  // "measured {date}"
  // Scope pill shared by NumberWall + HomeFooter
  | 'gghere.pill.hkOnly'
  // Honest FAQ
  | 'gghere.faq.h2'
  | 'gghere.faq.real-data.q'
  | 'gghere.faq.real-data.a'
  | 'gghere.faq.guide-scope.q'
  | 'gghere.faq.guide-scope.a'    // {scope}
  | 'gghere.faq.signage.q'
  | 'gghere.faq.signage.a'
  // Footer — closer + crawlable index
  | 'gghere.footer.closer.h2'
  | 'gghere.footer.closer.pickCity'
  | 'gghere.footer.closer.browseAll'
  | 'gghere.footer.roster.h3'     // also the nav aria-label
  | 'gghere.footer.roster.counts' // {cityCount} · {planetTotal} · {continentCount}
  | 'gghere.footer.roster.planetsUnit'  // sr-only unit after each count
  | 'gghere.footer.attribution'   // licence-required attribution sentence
  | 'gghere.footer.family'
  | 'gghere.footer.newTab'
  | 'gghere.carousel.aria'
  | 'gghere.carousel.flyIn'
  | 'gghere.carousel.cardAlt'
  | 'gghere.carousel.planets'
  | 'gghere.carousel.enter'
  | 'gghere.carousel.prev'
  | 'gghere.carousel.next'
  | 'gghere.carousel.position'
  | 'gghere.carousel.cursor.drag'
  // Planet HUD chrome (standing controls + aria). Place names stay English.
  | 'planet.hud.weather'
  // The one dial (ADR-0367): quiet / ambient / live.
  | 'planet.live.quiet'
  | 'planet.live.ambient'
  | 'planet.live.live'
  | 'planet.live.aria'
  | 'planet.card.more'
  | 'planet.hud.weather.day'
  | 'planet.hud.weather.golden'
  | 'planet.hud.weather.night'
  | 'planet.hud.weather.rain'
  | 'planet.hud.weather.typhoon'
  | 'planet.hud.weather.undersea'
  | 'planet.hud.column'
  | 'planet.hud.stars'
  | 'planet.hud.stars.over'
  | 'planet.hud.stars.lines'
  | 'planet.hud.stars.names'
  | 'planet.hud.stars.exit'
  | 'planet.hud.stars.hint'
  | 'planet.scan.landmark'
  | 'planet.scan.landmark.height'
  | 'planet.scan.landmark.storeys'
  | 'planet.scan.landmark.footprint'
  | 'planet.scan.landmark.crowns'
  | 'planet.scan.landmark.stamp'
  | 'planet.scan.landmark.stampNew'
  // Look-around landmark rooms (ADR-0345): the "· inside" mark + the viewer chrome
  | 'planet.scan.landmark.inside'
  | 'planet.room.catalog'
  | 'planet.room.catalog.hint'
  | 'planet.room.catalog.empty'
  | 'planet.room.catalog.show'
  | 'planet.room.catalog.visited'
  | 'planet.room.catalog.rain'
  | 'planet.room.catalog.storm'
  | 'planet.room.catalog.nearby'
  | 'planet.room.enter'
  | 'planet.room.walkUp'
  | 'planet.room.leave'
  | 'planet.room.credit'
  | 'planet.room.stamp'
  | 'planet.room.closed'
  | 'planet.room.opening'
  | 'planet.room.opening.named'
  | 'planet.room.opening.dream'
  | 'planet.room.look'
  | 'planet.room.view.controls'
  | 'planet.room.view.window'
  | 'planet.room.view.interior'
  | 'planet.room.view.zoomOut'
  | 'planet.room.view.zoomIn'
  | 'planet.room.view.city'
  | 'planet.room.view.city.hint'
  | 'planet.room.photo'
  | 'planet.room.photo.aria'
  | 'planet.room.photo.saved'
  | 'planet.room.photo.retry'
  | 'planet.room.frame.empty'
  | 'planet.room.frame.hang'
  | 'planet.room.frame.clear'
  | 'planet.room.frame.close'
  | 'planet.room.frame.paint'
  | 'planet.room.frame.guests'
  | 'planet.room.frame.custom'
  | 'planet.room.frame.pick'
  | 'planet.room.frame.hint.custom'
  | 'planet.room.frame.failed'
  | 'planet.room.frame.keep'
  | 'planet.room.frame.hint'
  | 'planet.room.frame.with'
  | 'planet.room.floors'
  | 'planet.room.floor.lobby'
  | 'planet.room.floor.mid'
  | 'planet.room.floor.sky'
  | 'planet.room.floor.deck'
  | 'planet.room.floor.hall'
  | 'planet.room.floor.dream'
  | 'planet.room.paint'
  | 'planet.room.paint.aria'
  | 'planet.room.paint.sheet'
  | 'planet.room.paint.interior'
  | 'planet.room.paint.windows'
  | 'planet.room.paint.closed'
  | 'planet.room.paint.price'
  | 'planet.room.paint.hint'
  | 'planet.room.paint.official'
  | 'planet.room.paint.followHud'
  | 'planet.room.paint.live'
  | 'planet.room.paint.live.aria'
  | 'planet.room.paint.signin'
  | 'planet.room.paint.insufficient'
  | 'planet.room.paint.working'
  | 'planet.room.paint.done'
  | 'planet.room.paint.enterMine'
  | 'planet.room.paint.mine'
  | 'planet.room.paint.community'
  | 'planet.room.paint.by'
  | 'planet.room.paint.publish'
  | 'planet.room.paint.busy'
  | 'planet.room.paint.failed'
  | 'planet.room.paint.have'
  | 'planet.room.upload'
  | 'planet.room.upload.closed'
  | 'planet.room.upload.signin'
  | 'planet.room.upload.insufficient'
  | 'planet.room.upload.working'
  | 'planet.room.upload.done'
  | 'planet.room.upload.badRatio'
  | 'planet.room.paint.lost'
  | 'planet.room.paint.noKit'
  | 'planet.room.paint.tier'
  | 'planet.room.paint.slot'
  | 'planet.room.upload.failed'
  | 'planet.room.paint.elsewhere'
  | 'planet.room.beat.windows'
  | 'planet.room.beat.windows.dream'
  | 'planet.room.beat.windows.harbour'
  | 'planet.room.beat.windows.canal'
  | 'planet.room.beat.windows.rooftops'
  | 'planet.room.beat.windows.iron'
  | 'planet.room.beat.windows.needle'
  | 'planet.room.beat.windows.desert'
  | 'planet.room.beat.windows.mountain'
  | 'planet.room.beat.windows.garden'
  | 'planet.room.beat.windows.street'
  | 'planet.room.beat.wall'
  | 'planet.room.beat.sky'
  | 'planet.room.beat.floor'
  | 'planet.scan.landmark.sky'
  | 'planet.scan.landmark.photo'
  | 'planet.scan.building'
  | 'planet.scan.inspect'
  | 'planet.scan.building.footprint'
  | 'planet.scan.building.height'
  | 'planet.scan.building.floors'
  | 'planet.scan.building.noHeight'
  | 'planet.scan.lot.fallen'
  | 'planet.scan.lot.stood'
  | 'planet.scan.lot.rebuilt'
  | 'planet.scan.lot.rebuild'
  | 'planet.scan.lot.sheet'
  | 'planet.scan.lot.tiers'
  | 'planet.scan.lot.crown'
  | 'planet.scan.lot.crown.flat'
  | 'planet.scan.lot.crown.spire'
  | 'planet.scan.lot.crown.dome'
  | 'planet.scan.lot.crown.ring'
  | 'planet.scan.lot.height'
  | 'planet.scan.lot.tone'
  | 'planet.scan.lot.confirm'
  | 'planet.scan.lot.short'
  | 'planet.scan.lot.close'
  | 'planet.scan.lot.publishSignin'
  | 'planet.scan.skin'
  | 'planet.scan.skin.sheet'
  | 'planet.scan.skin.pick'
  | 'planet.scan.skin.hint'
  | 'planet.scan.skin.apply'
  | 'planet.scan.skin.remove'
  | 'planet.scan.skin.signin'
  | 'planet.scan.skin.failed'
  | 'planet.scan.skin.saved'
  | 'planet.scan.skin.mode.poster'
  | 'planet.scan.skin.mode.tile'
  | 'planet.scan.skin.scale.1'
  | 'planet.scan.skin.scale.5'
  | 'planet.scan.skin.hint.poster'
  | 'planet.scan.building.replace'
  | 'planet.scan.building.sheet'
  | 'planet.scan.building.pick'
  | 'planet.scan.building.hint'
  | 'planet.scan.building.working'
  | 'planet.scan.building.done'
  | 'planet.scan.building.failed'
  | 'planet.scan.building.tier'
  | 'planet.scan.building.busy'
  | 'planet.scan.building.restore'
  | 'planet.scan.building.signin'
  | 'planet.scan.building.survey'
  | 'planet.scan.building.surveyHint'
  | 'planet.scan.building.surveying'
  | 'planet.scan.building.surveyUnavailable'
  | 'planet.scan.building.surveySignin'
  | 'planet.scan.building.source'
  | 'planet.scan.building.registerHeight'
  | 'planet.scan.building.registerStoreys'
  | 'planet.scan.building.thumbPlaceholder'
  | 'planet.scan.plot.title'
  | 'planet.scan.plot.hint'
  | 'planet.scan.plot.build'
  | 'planet.scan.plot.working'
  | 'planet.scan.plot.done'
  | 'planet.scan.plot.empty'
  | 'planet.scan.plot.probe.loading'
  | 'planet.scan.plot.probe.found'
  | 'planet.scan.plot.probe.unnamed'
  | 'planet.scan.plot.buildNamed'
  | 'planet.scan.plot.ju'
  | 'planet.scan.plot.ding'
  | 'planet.scan.plot.dingHint'
  | 'planet.scan.plot.dingBuild'
  | 'planet.scan.plot.dingWorking'
  | 'planet.scan.plot.dingDone'
  | 'planet.scan.plot.home'
  | 'planet.scan.plot.homeHint'
  | 'planet.scan.plot.house'
  | 'planet.scan.plot.villa'
  | 'planet.scan.plot.homeWorking'
  | 'planet.scan.plot.homeDone'
  | 'planet.scan.building.facadeNote'
  | 'planet.scan.building.leaseFor'
  | 'planet.scan.building.leaseUntil'
  | 'planet.scan.building.reskin'
  | 'planet.scan.building.appearance'
  | 'planet.scan.building.facade'
  | 'planet.scan.building.mass'
  | 'planet.scan.building.indoor'
  | 'planet.scan.building.download'
  | 'planet.scan.building.downloadPay'
  | 'planet.scan.building.downloadHint'
  | 'planet.scan.building.renew'
  | 'planet.scan.building.ju'
  | 'planet.scan.creation.sheet'
  | 'planet.scan.creation.hint'
  | 'planet.scan.creation.next'
  | 'planet.scan.creation.skin'
  | 'planet.scan.creation.rebuild'
  | 'planet.scan.creation.building'
  | 'planet.scan.creation.room'
  | 'planet.scan.creation.placename'
  | 'planet.scan.creation.lookroom'
  | 'planet.scan.creation.highlight'
  | 'planet.scan.unnamed.title'
  | 'planet.scan.unnamed.hint'
  | 'planet.scan.unnamed.nameEn'
  | 'planet.scan.unnamed.nameZh'
  | 'planet.scan.unnamed.saveName'
  | 'planet.scan.unnamed.saveNameFree'
  | 'planet.scan.unnamed.look'
  | 'planet.scan.unnamed.lookFree'
  | 'planet.scan.unnamed.lookHint'
  | 'planet.scan.unnamed.lookFile'
  | 'planet.scan.unnamed.publish'
  | 'planet.scan.unnamed.signin'
  | 'planet.scan.unnamed.saved'
  | 'planet.scan.unnamed.insufficient'
  | 'planet.scan.unnamed.brand'
  | 'planet.scan.unnamed.badName'
  | 'planet.scan.unnamed.notEquirect'
  | 'planet.scan.unnamed.tooLarge'
  | 'planet.scan.unnamed.failed'
  | 'planet.scan.arrange.title'
  | 'planet.scan.arrange.hint'
  | 'planet.scan.arrange.floor'
  | 'planet.scan.arrange.wall'
  | 'planet.scan.arrange.slat'
  | 'planet.scan.arrange.fabric'
  | 'planet.scan.arrange.sun'
  | 'planet.scan.arrange.save'
  | 'planet.scan.arrange.feel'
  | 'planet.scan.arrange.wood'
  | 'planet.scan.arrange.stone'
  | 'planet.scan.arrange.paint'
  | 'planet.scan.arrange.cloth'
  | 'planet.scan.arrange.bright'
  | 'planet.scan.arrange.cozy'
  | 'planet.scan.arrange.moody'
  | 'planet.scan.arrange.level'
  | 'planet.scan.arrange.levelsHint'
  | 'planet.scan.furnish.title'
  | 'planet.scan.furnish.hint'
  | 'planet.scan.furnish.cap'
  | 'planet.scan.furnish.full'
  | 'planet.scan.furnish.empty'
  | 'planet.scan.furnish.bed'
  | 'planet.scan.furnish.table'
  | 'planet.scan.furnish.stool'
  | 'planet.scan.furnish.chest'
  | 'planet.scan.furnish.lamp'
  | 'planet.scan.creation.private'
  | 'planet.scan.creation.pending'
  | 'planet.scan.creation.public'
  | 'planet.scan.creation.rejected'
  | 'planet.scan.creation.publish'
  | 'planet.scan.creation.withdraw'
  | 'planet.scan.creation.delete'
  | 'planet.scan.creation.none'
  | 'planet.scan.creation.signin'
  | 'planet.scan.creation.window.title'
  | 'planet.scan.creation.window.1h'
  | 'planet.scan.creation.window.1d'
  | 'planet.scan.creation.window.1w'
  | 'planet.scan.creation.window.1m'
  | 'planet.scan.creation.window.1y'
  | 'planet.scan.creation.window.price'
  | 'planet.scan.creation.window.free'
  | 'planet.scan.creation.window.tier'
  | 'planet.scan.creation.window.forfeit'
  | 'planet.scan.creation.window.balance'
  | 'planet.scan.creation.window.insufficient'
  | 'planet.scan.creation.window.until'
  | 'planet.scan.creation.window.paid'
  | 'planet.scan.creation.window.slot'
  | 'planet.scan.creation.window.pendingAfter'
  | 'planet.scan.creation.window.expired'
  | 'planet.scan.creation.window.unpaid'
  | 'planet.scan.creation.window.failed'
  | 'planet.scan.creation.window.caps'
  | 'planet.scan.ghost.skin'
  | 'planet.scan.ghost.rebuilt'
  | 'planet.scan.ghost.building'
  | 'planet.hud.allCity'
  | 'planet.hud.veil.planet'
  | 'planet.hud.veil.flat'
  | 'planet.hud.loading.daily'
  | 'planet.hud.loading'
  | 'planet.hud.loading.district'
  | 'planet.hud.floor'
  | 'planet.hud.standing'
  | 'planet.hud.locate'
  | 'planet.hud.homeStamp.aria'
  | 'planet.julai.sit'
  | 'planet.julai.stand'
  | 'planet.julai.post.claim'
  | 'planet.julai.post.drop'
  | 'planet.julai.post.open'
  | 'planet.julai.isle'
  | 'planet.julai.home'
  | 'planet.julai.tend'
  | 'planet.julai.sow'
  | 'planet.julai.harvest'
  | 'planet.julai.climb'
  | 'planet.julai.descend'
  | 'planet.julai.down'
  | 'planet.julai.dive'
  | 'planet.julai.surface'
  | 'planet.julai.go'
  | 'planet.hud.shore'
  | 'planet.hud.skywalk'
  | 'planet.hud.skywalk.on'
  | 'planet.hud.play.courier'
  | 'planet.hud.play.rally'
  | 'planet.hud.play.kart'
  | 'planet.hud.play.drive'
  | 'planet.hud.play.park'
  | 'planet.hud.play.boat'
  | 'planet.hud.play.sail'
  | 'planet.hud.kart'
  | 'planet.hud.drive'
  | 'planet.hud.park'
  | 'planet.hud.boat'
  | 'planet.hud.sail'
  | 'planet.hud.castOff'
  | 'planet.hud.play.castOff'
  | 'planet.hud.play.siege'
  | 'planet.hud.freedom'
  | 'planet.hud.freedom.aria'
  | 'planet.hud.sculpt'
  | 'planet.hud.sculpt.aria'
  | 'planet.hud.play.ground'
  | 'planet.hud.play.carnival'
  | 'planet.hud.play.allsports'
  | 'planet.freedom.title'
  | 'planet.freedom.close'
  | 'planet.freedom.empty'
  | 'planet.freedom.underfoot'
  | 'planet.freedom.near'
  | 'planet.freedom.play'
  | 'planet.freedom.dress'
  | 'planet.freedom.paint'
  | 'planet.freedom.kit'
  | 'planet.freedom.weather'
  | 'planet.freedom.block.open'
  | 'planet.freedom.block.pocket-park'
  | 'planet.freedom.block.plaza'
  | 'planet.freedom.block.high-street'
  | 'planet.freedom.block.beach'
  | 'planet.freedom.block.waterfront'
  | 'planet.freedom.opinion.open'
  | 'planet.freedom.opinion.pocket-park'
  | 'planet.freedom.opinion.plaza'
  | 'planet.freedom.opinion.high-street'
  | 'planet.freedom.opinion.beach'
  | 'planet.freedom.opinion.waterfront'
  | 'planet.freedom.civic.vegetation'
  | 'planet.freedom.civic.plaza'
  | 'planet.freedom.civic.road'
  | 'planet.freedom.civic.sand'
  | 'planet.freedom.civic.water'
  | 'planet.freedom.kick'
  | 'planet.freedom.trial'
  | 'planet.freedom.score'
  | 'planet.freedom.beat'
  | 'planet.freedom.finish'
  | 'planet.freedom.bond'
  | 'planet.freedom.bondVersus'
  | 'planet.freedom.bondWaiting'
  | 'planet.freedom.bondGym'
  | 'planet.freedom.bondWin'
  | 'planet.freedom.bondLose'
  | 'planet.freedom.bondDraw'
  | 'planet.freedom.bondPick'
  | 'planet.freedom.bondShare'
  | 'planet.freedom.bondRoom'
  | 'planet.freedom.bondHall'
  | 'planet.freedom.bondWatch'
  | 'planet.freedom.bondBadge'
  | 'planet.freedom.bondSuper'
  | 'planet.freedom.bondResisted'
  | 'planet.freedom.bondReady'
  | 'planet.freedom.bondStart'
  | 'planet.freedom.bondStrike'
  | 'planet.freedom.bondAuto'
  | 'planet.freedom.bondCast'
  | 'planet.freedom.jevAttack'
  | 'planet.freedom.jevRetreat'
  | 'planet.freedom.jevCover'
  | 'planet.freedom.jevHold'
  | 'planet.freedom.jevHeal'
  | 'planet.freedom.jevExplore'
  | 'planet.freedom.el.fire'
  | 'planet.freedom.el.water'
  | 'planet.freedom.el.wood'
  | 'planet.freedom.el.metal'
  | 'planet.freedom.el.earth'
  | 'planet.freedom.el.wind'
  | 'planet.freedom.el.spirit'
  | 'planet.sculpt.title'
  | 'planet.sculpt.close'
  | 'planet.sculpt.blurb'
  | 'planet.sculpt.raise'
  | 'planet.sculpt.lower'
  | 'planet.sculpt.flatten'
  | 'planet.sculpt.underfoot'
  | 'planet.sculpt.delta'
  | 'planet.carnival.title'
  | 'planet.carnival.blurb'
  | 'planet.carnival.play'
  | 'planet.allsports.title'
  | 'planet.allsports.blurb'
  | 'planet.allsports.visit'
  | 'planet.hud.coast'
  | 'planet.hud.coast.aria'
  | 'planet.coast.title'
  | 'planet.coast.close'
  | 'planet.coast.beach'
  | 'planet.coast.spline'
  | 'planet.coast.clear'
  | 'planet.coast.empty'
  | 'planet.coast.needCell'
  | 'planet.coast.sport.honesty'
  | 'planet.coast.games.honesty'
  | 'planet.carnival.talk'
  | 'planet.carnival.attendant'
  | 'planet.carnival.opponent'
  | 'planet.hud.play'
  | 'planet.hud.rally'
  | 'planet.hud.siege'
  | 'planet.hud.view'
  | 'planet.hud.immersive'
  | 'planet.hud.immersive.aria'
  | 'planet.hud.overview'
  | 'planet.hud.overview.aria'
  | 'planet.hud.more'
  | 'planet.hud.more.hide'
  | 'planet.hud.actions'
  | 'planet.hud.actions.hide'
  | 'planet.hud.stats'
  | 'planet.hud.stats.on'
  | 'planet.hud.stats.off'
  | 'planet.hud.stats.gpc'
  | 'planet.hud.cloud.synced'
  | 'planet.hud.cloud.syncedShort'
  | 'planet.hud.cloud.signIn'
  | 'planet.hud.cloud.signInShort'
  | 'planet.hud.browse'
  | 'planet.hud.choose'
  | 'planet.map.cube'
  | 'planet.map.here'
  | 'planet.map.all'
  | 'planet.map.switch'
  | 'planet.picker.roster'
  | 'planet.hud.pin'
  | 'planet.hud.pinHold'
  | 'planet.hud.drop'
  | 'planet.hud.drop.aria'
  | 'planet.hud.home'
  | 'planet.hud.home.aria'
  | 'planet.hud.scan'
  | 'planet.hud.scanBadge'
  | 'planet.hud.nav'
  | 'planet.hud.districts'
  | 'planet.hud.move'
  | 'planet.hud.accelerate'
  | 'planet.hud.forward'
  | 'planet.hud.left'
  | 'planet.hud.right'
  | 'planet.hud.back'
  | 'planet.hud.reverse'
  | 'planet.hud.jump'
  | 'planet.hud.hop'
  | 'planet.hud.gas'
  | 'planet.hud.brake'
  | 'planet.hud.drift'
  | 'planet.hud.drift.aria'
  | 'planet.hud.turbo'
  | 'planet.hud.turbo.aria'
  | 'planet.hud.camera'
  | 'planet.hud.shop'
  | 'planet.hud.house'
  | 'planet.hud.house.hint'
  | 'planet.hud.house.dive'
  | 'planet.hud.house.seeking'
  | 'planet.hud.house.none'
  | 'planet.hud.house.done'
  | 'planet.hud.photo'
  | 'planet.hud.photo.aria'
  | 'planet.hud.photo.saved'
  | 'planet.hud.photo.retry'
  | 'planet.hud.photo.busy'
  | 'planet.hud.photo.lapse'
  | 'planet.hud.lineup'
  | 'planet.hud.lineup.aria'
  | 'planet.hud.lineup.title'
  | 'planet.hud.lineup.reading'
  | 'planet.hud.lineup.placing'
  | 'planet.hud.lineup.noGps'
  | 'planet.hud.lineup.incomplete'
  | 'planet.hud.lineup.unreadable'
  | 'planet.hud.lineup.fix.file'
  | 'planet.hud.lineup.fix.noCompass'
  | 'planet.hud.lineup.fix.compassTrue'
  | 'planet.hud.lineup.fix.compassMag'
  | 'planet.hud.lineup.fix.compassUnset'
  | 'planet.hud.lineup.gpsOff'
  | 'planet.hud.lineup.gpsUnavailable'
  | 'planet.hud.lineup.split'
  | 'planet.hud.lineup.splitPlaces'
  | 'planet.hud.lineup.match.gps'
  | 'planet.hud.lineup.match.planet'
  | 'planet.hud.lineup.match.photo'
  | 'planet.hud.lineup.match.gpsOff'
  | 'planet.hud.lineup.match.gpsUnavailable'
  | 'planet.hud.lineup.match.gpsWait'
  | 'planet.hud.lineup.match.noPlanet'
  | 'planet.hud.lineup.match.offBelt'
  | 'planet.hud.lineup.away'
  | 'planet.hud.lineup.awayHere'
  | 'planet.hud.lineup.go'
  | 'planet.hud.lineup.pair'
  | 'planet.hud.lineup.sync'
  | 'planet.hud.lineup.synced'
  | 'planet.hud.lineup.syncMiss'
  | 'planet.hud.lineup.travelling'
  | 'planet.hud.lineup.stoodTrue'
  | 'planet.hud.lineup.stoodTrueAcc'
  | 'planet.hud.lineup.stoodMag'
  | 'planet.hud.lineup.stoodUnspecified'
  | 'planet.hud.lineup.stoodNone'
  | 'planet.hud.lineup.stoodLoose'
  | 'planet.hud.lineup.stoodUnapplied'
  | 'planet.hud.lineup.refused'
  | 'planet.hud.lineup.close'
  | 'planet.hud.lineup.ask'
  | 'planet.hud.lineup.allow'
  | 'planet.hud.lineup.camera'
  | 'planet.hud.lineup.camera.aria'
  | 'planet.hud.lineup.take'
  | 'planet.hud.lineup.notNow'
  | 'planet.hud.lineup.place'
  | 'planet.hud.lineup.placePh'
  | 'planet.hud.lineup.placeGo'
  | 'planet.hud.lineup.placeBlank'
  | 'planet.hud.lineup.eye'
  | 'planet.hud.lineup.eyeRefused'
  | 'planet.hud.lineup.massAhead'
  | 'planet.hud.lineup.massAheadH'
  | 'planet.hud.lineup.massNear'
  | 'planet.hud.lineup.massNearH'
  | 'planet.hud.lineup.massNone'
  | 'planet.hud.lineup.massName'
  | 'planet.hud.lineup.stoodLink'
  | 'planet.hud.lineup.stoodLinkHead'
  | 'planet.hud.lineup.stoodLinkMiss'
  | 'planet.hud.lineup.linkAway'
  | 'planet.hud.lineup.linkLabel'
  | 'planet.hud.lineup.linkRefused'
  | 'planet.hud.lineup.shoot'
  | 'planet.hud.lineup.shotSaved'
  | 'planet.hud.lineup.shotRetry'
  | 'planet.hud.lineup.frameStreet'
  | 'planet.hud.lineup.frameBack'
  | 'planet.hud.lineup.frameNote'
  | 'planet.hud.lineup.copy'
  | 'planet.hud.lineup.copied'
  | 'planet.hud.lineup.copyFailed'
  | 'planet.hud.portrait'
  | 'planet.hud.portrait.aria'
  | 'planet.hud.pet'
  | 'planet.hud.giant'
  | 'planet.hud.pedestrian'
  | 'planet.hud.mini'
  | 'planet.hud.scale.aria'
  | 'planet.hud.surface'
  | 'planet.hud.interior'
  | 'planet.hud.seamScreens'
  | 'planet.hud.seamScreens.aria'
  | 'planet.hud.seam.hint'
  | 'planet.hud.seam.book'
  | 'planet.hud.seam.pick'
  | 'planet.hud.seam.play'
  | 'planet.hud.seam.reading'
  | 'planet.hud.seam.visit'
  | 'planet.hud.seam.priceAtPublish'
  | 'planet.hud.seamMedia.hint'
  | 'planet.hud.seamMedia.count'
  | 'planet.hud.seamMedia.type'
  | 'planet.hud.seamMedia.codec'
  | 'planet.hud.seamMedia.size'
  | 'planet.hud.seamMedia.motion'
  | 'planet.hud.seamMedia.long'
  | 'planet.hud.seamMedia.picked'
  | 'planet.hud.interiorSea'
  | 'planet.hud.interiorSea.aria'
  | 'planet.sea.fire.glyph'
  | 'planet.sea.fire'
  | 'planet.sea.yard.glyph'
  | 'planet.sea.yard'
  | 'planet.sea.again.glyph'
  | 'planet.sea.again'
  | 'planet.sea.weapon.lance.glyph'
  | 'planet.sea.weapon.lance'
  | 'planet.sea.weapon.ribbon.glyph'
  | 'planet.sea.weapon.ribbon'
  | 'planet.sea.weapon.mortar.glyph'
  | 'planet.sea.weapon.mortar'
  | 'planet.sea.weapon.depth.glyph'
  | 'planet.sea.weapon.depth'
  | 'planet.sea.beat.skiff'
  | 'planet.sea.beat.cutter'
  | 'planet.sea.beat.hulk'
  | 'planet.sea.beat.boss'
  | 'planet.sea.wave'
  | 'planet.sea.afloat'
  | 'planet.sea.incoming'
  | 'planet.sea.next'
  | 'planet.sea.crew.gunner'
  | 'planet.sea.crew.bosun'
  | 'planet.sea.crew.carpenter'
  | 'planet.sea.crew.raise'
  | 'planet.sea.crew.full'
  | 'planet.sea.crew.choose'
  | 'planet.sea.crew.open'
  | 'planet.sea.crew.close'
  | 'planet.sea.crew.balance'
  | 'planet.sea.crew.current'
  | 'planet.sea.crew.next'
  | 'planet.sea.crew.train'
  | 'planet.sea.crew.max'
  | 'planet.sea.crew.reload'
  | 'planet.sea.plate.0'
  | 'planet.sea.plate.1'
  | 'planet.sea.plate.2'
  | 'planet.sea.plate.3'
  | 'planet.sea.note.need'
  | 'planet.sea.note.kept'
  | 'planet.sea.note.sunk'
  | 'planet.sea.note.hit'
  | 'planet.sea.note.boss'
  | 'planet.sea.note.settle'
  | 'planet.sea.note.gunner'
  | 'planet.sea.note.bosun'
  | 'planet.sea.note.carpenter'
  | 'planet.sea.note.plate'
  | 'planet.sea.fire.aria'
  | 'planet.sea.weapon.aria'
  | 'planet.sea.keep.aria'
  | 'planet.sea.boost.aria'
  | 'planet.sea.hull.aria'
  | 'planet.sea.crew.aria'
  | 'planet.sea.yard.aria'
  | 'planet.sea.again.aria'
  | 'planet.sea.keys'
  | 'planet.sea.tab.weapon.glyph'
  | 'planet.sea.tab.weapon'
  | 'planet.sea.tab.hull.glyph'
  | 'planet.sea.tab.hull'
  | 'planet.sea.tab.girl.glyph'
  | 'planet.sea.tab.girl'
  | 'planet.sea.tab.pet.glyph'
  | 'planet.sea.tab.pet'
  | 'planet.sea.tab.trade.glyph'
  | 'planet.sea.tab.trade'
  | 'planet.sea.tab.ride.glyph'
  | 'planet.sea.tab.ride'
  | 'planet.sea.yard.centre.glyph'
  | 'planet.sea.yard.centre'
  | 'planet.sea.yard.ether.glyph'
  | 'planet.sea.yard.ether'
  | 'planet.sea.yard.back.glyph'
  | 'planet.sea.yard.back'
  | 'planet.sea.yard.see'
  | 'planet.sea.yard.close.aria'
  | 'planet.sea.result.close.aria'
  | 'planet.sea.tutorial.close.aria'
  | 'planet.sea.panel.collapse.aria'
  | 'planet.sea.panel.expand.aria'
  | 'planet.sea.yard.centre.aria'
  | 'planet.sea.yard.ether.aria'
  | 'planet.sea.yard.port'
  | 'planet.sea.yard.roster'
  | 'planet.sea.yard.drag'
  | 'planet.sea.yard.noPet'
  | 'planet.sea.yard.none'
  | 'planet.sea.yard.offChart'
  | 'planet.sea.yard.tradeBlurb'
  | 'planet.sea.yard.petBlurb'
  | 'planet.sea.yard.rideBlurb'
  | 'planet.sea.yard.rideEmpty'
  | 'planet.sea.yard.crewBlurb'
  | 'planet.sea.yard.doorNote'
  | 'planet.sea.yard.crew.glyph'
  | 'planet.sea.yard.crew'
  | 'planet.sea.yard.weapon.glyph'
  | 'planet.sea.yard.weapon'
  | 'planet.sea.yard.crew.aria'
  | 'planet.sea.yard.weapon.aria'
  | 'planet.sea.yard.weaponPrice'
  | 'planet.sea.yard.weaponTip'
  | 'planet.sea.yard.weaponTipKept'
  | 'planet.sea.yard.weaponKeep'
  | 'planet.sea.yard.weaponCycle'
  | 'planet.sea.yard.weaponShopTitle'
  | 'planet.sea.yard.weaponKeptBadge'
  | 'planet.sea.yard.weaponEmbedHint'
  | 'planet.sea.yard.weaponLevelBadge'
  | 'planet.sea.yard.weaponUpgrade'
  | 'planet.sea.yard.weaponLevelMax'
  | 'planet.sea.yard.berthStatus'
  | 'planet.sea.yard.berthStatusPaid'
  | 'planet.sea.yard.powderStatus'
  | 'planet.sea.yard.powderStatusFree'
  | 'planet.sea.yard.captainHire'
  | 'planet.sea.yard.captainOwned'
  | 'planet.sea.yard.goldShopTitle'
  | 'planet.sea.yard.goldSinkBuy'
  | 'planet.sea.yard.goldSinkOwned'
  | 'planet.sea.yard.petCosmeticBuy'
  | 'planet.sea.yard.petCosmeticOwned'
  | 'planet.sea.yard.rideSkinBuy'
  | 'planet.sea.yard.rideSkinOwned'
  | 'planet.sea.yard.rideSpecialForm'
  | 'planet.sea.yard.autopilotStrip'
  | 'planet.sea.yard.autopilotCta'
  | 'planet.sea.yard.combatAutoFireStrip'
  | 'planet.sea.yard.combatAutoFireCta'
  | 'planet.sea.yard.combatAutoFireOn'
  | 'planet.sea.autoFire.enable'
  | 'planet.sea.autoFire.enable.aria'
  | 'planet.sea.autoFire.on'
  | 'planet.sea.yard.seasonShopTitle'
  | 'planet.sea.yard.seasonCosmeticBuy'
  | 'planet.sea.yard.seasonCosmeticOwned'
  | 'planet.sea.yard.rig.glyph'
  | 'planet.sea.yard.rig'
  | 'planet.sea.yard.owned.glyph'
  | 'planet.sea.yard.owned'
  | 'planet.sea.yard.upgrade'
  | 'planet.sea.yard.coinHonesty'
  | 'planet.sea.yard.lock'
  | 'planet.sea.stat.mass'
  | 'planet.sea.stat.speed'
  | 'planet.sea.stat.shot'
  | 'planet.sea.stat.hull'
  | 'planet.sea.dock'
  | 'planet.sea.banner.wave'
  | 'planet.sea.banner.boss'
  | 'planet.sea.banner.bossDown'
  | 'planet.sea.boss.aria'
  | 'planet.sea.result.title'
  | 'planet.sea.result.sunk'
  | 'planet.sea.result.wave'
  | 'planet.sea.result.purse'
  | 'planet.sea.result.best'
  | 'planet.sea.result.tradeOff'
  | 'planet.sea.result.newBest'
  | 'planet.sea.tutorial.title'
  | 'planet.sea.tutorial.goal'
  | 'planet.sea.tutorial.sail'
  | 'planet.sea.tutorial.fire'
  | 'planet.sea.tutorial.nitro'
  | 'planet.sea.tutorial.hop'
  | 'planet.sea.tutorial.touch'
  | 'planet.sea.tutorial.ok'
  | 'planet.sea.skill.aria'
  | 'planet.sea.skill.cast.aria'
  | 'planet.sea.skill.deckVolley'
  | 'planet.sea.skill.deckBurst'
  | 'planet.sea.skill.deckMend'
  | 'planet.hud.shell.cavity'
  | 'planet.hud.shell.undersea'
  | 'planet.hud.shell.interior'
  | 'planet.hud.shell.core'
  | 'planet.hud.shell.loco.sail'
  | 'planet.hud.shell.loco.swim'
  | 'planet.hud.shell.loco.stand'
  | 'planet.hud.shell.loco.float'
  | 'planet.hud.shell.loco.look'
  | 'planet.hud.shell.dive'
  | 'planet.hud.shell.surface'
  | 'planet.hud.walk'
  | 'planet.hud.ride'
  | 'planet.hud.swap'
  | 'planet.hud.switchVehicle'
  | 'planet.hud.hopOff'
  | 'planet.hud.rideVessel'
  | 'planet.hud.vessel.train'
  | 'planet.hud.vessel.ferry'
  | 'planet.hud.vessel.ship'
  | 'planet.hud.travel'
  | 'planet.gate.title'
  | 'planet.gate.body'
  | 'planet.gate.close'
  | 'planet.hud.enter'
  | 'planet.hud.dive'
  | 'planet.fare.toilet.urgent'
  | 'planet.fare.toilet.polite'
  | 'planet.fare.tap.thirsty'
  | 'planet.fare.tap.bottle'
  | 'planet.fare.bay.late'
  | 'planet.fare.bay.circling'
  // Pin + chat chrome (place names stay data)
  | 'planet.ask.pinned'
  | 'planet.ask.closePin'
  | 'planet.ask.scanning'
  | 'planet.ask.empty'
  | 'planet.ask.told'
  | 'planet.ask.carParks'
  | 'planet.ask.nearby'
  | 'planet.ask.askSpot'
  | 'planet.ask.readSpot'
  | 'planet.ask.isoMap'
  | 'planet.ask.daily'
  | 'planet.ask.news'
  | 'planet.ask.terminal'
  | 'planet.ask.dseekGroup'
  | 'planet.ask.daily.aria'
  | 'planet.ask.news.aria'
  | 'planet.ask.terminal.aria'
  | 'planet.ask.title'
  | 'planet.ask.closeChat'
  | 'planet.ask.emptyHint'
  | 'planet.ask.placeholder'
  | 'planet.news.near'
  | 'planet.news.story'
  | 'planet.news.story.aria'
  | 'planet.news.door.aria'
  | 'planet.service.funNote'
  | 'planet.news.group'
  | 'planet.family.venueDoor'
  | 'planet.parking.priorities'
  | 'planet.parking.rule'
  | 'planet.parking.free'
  | 'planet.parking.meter'
  | 'planet.parking.carpark'
  | 'planet.parking.fromPin'
  | 'planet.parking.watchArea'
  | 'planet.parking.stopArea'
  | 'planet.parking.watching'
  | 'planet.parking.changed'
  | 'planet.parking.noSpaces'
  | 'planet.parking.targetLost'
  | 'planet.parking.stale'
  | 'planet.parking.alternatives'
  | 'planet.parking.directions'
  | 'planet.parking.useLocation'
  | 'planet.parking.stopLocation'
  | 'planet.parking.locationPending'
  | 'planet.parking.locationUnavailable'
  | 'planet.parking.locationNote'
  | 'planet.parking.near'
  | 'planet.parking.closer'
  | 'planet.parking.selected'
  | 'planet.parking.liveRequired'
  | 'planet.parking.unknown'
  | 'planet.kerb.observed'
  | 'planet.family.checked'
  | 'planet.family.refresh'
  | 'planet.family.refreshing'
  | 'planet.kerb.group'
  | 'planet.kerb.bays'
  | 'planet.kerb.rate'
  | 'planet.kerb.mood.open'
  | 'planet.kerb.mood.easy'
  | 'planet.kerb.mood.tight'
  | 'planet.kerb.mood.hopeless'
  | 'planet.kerb.mood.unknown'
  | 'planet.kerb.parks'
  | 'planet.kerb.nearby'
  | 'planet.kerb.arrivalCaveat'
  | 'planet.kerb.refresh'
  | 'planet.kerb.refreshing'
  | 'planet.kerb.watch'
  | 'planet.kerb.unwatch'
  | 'planet.kerb.watchLimit'
  | 'planet.kerb.watchNote'
  | 'planet.kerb.watchOpened'
  | 'planet.kerb.watchFull'
  | 'planet.kerb.watchDismiss'
  | 'planet.kerb.watchInspect'
  | 'planet.kerb.watchGroup'
  | 'planet.kerb.watchStopAll'
  | 'planet.flatmap.group'
  | 'planet.flatmap.title'
  | 'planet.flatmap.close'
  | 'planet.flatmap.canvas'
  | 'planet.flatmap.loading'
  | 'planet.flatmap.none'
  | 'planet.flatmap.none.body'
  | 'planet.flatmap.offframe'
  | 'planet.flatmap.dseek'
  | 'planet.flatmap.credit'
  | 'planet.venue.group'
  | 'planet.venue.hours'
  | 'planet.venue.credit'
  | 'planet.cityWeather.title'
  | 'planet.cityWeather.loading'
  | 'planet.cityWeather.unavailable'
  | 'planet.cityWeather.scope'
  | 'planet.cityWeather.issued'
  | 'planet.cityWeather.source'
  | 'planet.cityWeather.processing'
  | 'planet.schoolNet.group'
  | 'planet.schoolNet.primary'
  | 'planet.schoolNet.secondary'
  | 'planet.schoolNet.outside'
  | 'planet.schoolNet.note'
  | 'planet.schoolNet.door'
  | 'planet.schoolNet.credit'
  | 'planet.healthcare.group'
  | 'planet.healthcare.nearest'
  | 'planet.healthcare.note'
  | 'planet.healthcare.door'
  | 'planet.healthcare.credit'
  | 'planet.funAi.entertainment'
  | 'planet.funAi.guest'
  | 'planet.amenity.group'
  | 'planet.amenity.toilet'
  | 'planet.amenity.tap'
  | 'planet.amenity.temp.a'
  | 'planet.amenity.temp.c'
  | 'planet.amenity.temp.ac'
  | 'planet.amenity.temp.hc'
  | 'planet.amenity.temp.hac'
  | 'planet.amenity.onlyHot'
  | 'planet.amenity.fewHot'
  | 'planet.spot.group'
  | 'planet.spot.chip'
  | 'planet.spot.chip.aria'
  | 'planet.spot.credit'
  | 'planet.pitch.group'
  | 'planet.pitch.free'
  | 'planet.pitch.slots'
  | 'planet.pitch.weekend'
  | 'planet.pitch.report'
  | 'planet.pitch.preview'
  | 'planet.pitch.previewNone'
  | 'planet.pitch.totalNote'
  | 'planet.pitch.confirm'
  | 'planet.pitch.book'
  | 'planet.service.navigate'
  | 'planet.pitch.credit'
  | 'planet.harbour.group'
  | 'planet.harbour.count'
  | 'planet.harbour.last'
  | 'planet.harbour.last.at'
  | 'planet.harbour.top'
  | 'planet.harbour.credit'
  | 'planet.amenity.credit'
  // Cinema row (ADR-0367): official screenings only.
  | 'planet.cinema.group'
  | 'planet.cinema.tonight'
  | 'planet.cinema.notice'
  | 'planet.cinema.credit'
  // The Counting House research desk (ADR-0367).
  | 'planet.screen.desk.title'
  | 'planet.screen.desk.open'
  | 'planet.screen.desk.closed'
  | 'planet.screen.desk.verified'
  | 'planet.screen.desk.credit'
  | 'planet.screen.data.title'
  | 'planet.screen.data.hk'
  | 'planet.screen.data.us'
  | 'planet.screen.data.open'
  | 'planet.screen.data.close'
  | 'planet.screen.data.reload'
  | 'planet.screen.data.external'
  | 'planet.screen.data.loading'
  | 'planet.screen.data.hint'
  | 'planet.screen.data.predict'
  | 'planet.screen.predict.title'
  | 'planet.screen.predict.yes'
  | 'planet.screen.predict.ready'
  | 'planet.screen.predict.stale'
  | 'planet.screen.predict.loading'
  | 'planet.screen.predict.unavailable'
  | 'planet.screen.predict.fetched'
  | 'planet.screen.predict.source'
  | 'planet.screen.predict.open'
  // 🔎 address search (ADR-0367).
  | 'planet.company.none'
  | 'planet.company.unavailable'
  | 'planet.company.title'
  | 'planet.company.name'
  | 'planet.company.submit'
  | 'planet.company.unknown'
  | 'planet.company.loading'
  | 'planet.company.retrieved'
  | 'planet.company.external'
  | 'planet.search.open'
  | 'planet.search.aria'
  | 'planet.search.placeholder'
  | 'planet.search.none'
  | 'planet.search.unavailable'
  | 'planet.search.credit'
  | 'planet.search.credit.places'
  | 'planet.search.credit.gsi'
  | 'planet.search.aria.address'
  | 'planet.search.addressOnly'
  | 'planet.search.pin'
  | 'planet.search.walk'
  | 'planet.search.walkNote'
  | 'planet.search.on'
  | 'planet.search.go'
  | 'planet.search.edge'
  | 'planet.pin.scan'
  | 'planet.pin.ask'
  | 'planet.pin.centre'
  | 'planet.park.hub'
  | 'planet.park.free'
  | 'planet.park.metered'
  | 'planet.park.near'
  | 'planet.park.far'
  | 'planet.walk.line'
  | 'planet.walk.indoor'
  | 'planet.presentation.hk.landsd-location-search'
  | 'planet.presentation.hk.landsd-pedestrian-route'
  | 'planet.presentation.jp.gsi-address'
  | 'planet.brief.pill'
  | 'planet.brief.pill.aria'
  | 'planet.brief.title'
  | 'planet.brief.empty'
  | 'planet.brief.unavailable'
  | 'planet.brief.partial'
  | 'planet.brief.retry'
  | 'planet.brief.walk'
  | 'planet.brief.walk.aria'
  | 'planet.brief.close'
  // Jub Studio standing chrome (sidebar / icon rail / phone tab bar)
  | 'jub.nav.aria'
  | 'jub.nav.studio'
  | 'jub.nav.library'
  | 'jub.nav.more'
  | 'jub.nav.tapToOpen'
  | 'jub.nav.seeAllApps'
  | 'jub.nav.home'
  | 'jub.nav.theme.toLight'
  | 'jub.nav.theme.toDark'
  | 'jub.rail.aria'
  | 'jub.rail.settings'
  | 'jub.tab.aria'
  | 'jub.tab.create'
  | 'jub.tab.feed'
  | 'jub.tab.chat'
  | 'jub.tab.discover'
  | 'jub.tab.profile'
  | 'jub.tab.filter'
  | 'jub.primary.feed'
  | 'jub.primary.create'
  | 'jub.primary.filter'
  | 'jub.primary.chat'
  | 'jub.primary.discover'
  | 'jub.library.creations'
  | 'jub.library.liked'
  | 'jub.library.saved'
  | 'jub.more.create'
  | 'jub.more.imagine'
  | 'jub.more.chatlab'
  | 'jub.more.themes'
  | 'jub.more.book'
  | 'jub.more.stages'
  | 'jub.more.cards'
  | 'jub.more.poker'
  | 'jub.more.meshy'
  | 'jub.more.workshop'
  | 'jub.more.standee'
  // gghere leftovers + shared chrome
  | 'gghere.mode.pick'
  | 'gghere.mode.explore'
  | 'gghere.mode.explore.blurb'
  | 'gghere.mode.courier'
  | 'gghere.mode.courier.blurb'
  | 'gghere.mode.survival'
  | 'gghere.mode.survival.blurb'
  | 'gghere.mode.rally'
  | 'gghere.mode.rally.blurb'
  | 'gghere.chip.modeIn'
  | 'notfound.body'
  | 'notfound.back'
  | 'chrome.loading'
  | 'chrome.loading.named'
  | 'chrome.chunkError.body'
  | 'chrome.chunkError.reload';

/**
 * Values substituted into `{name}` slots of a message template by
 * `t(key, vars)`. Numbers are stringified with String() — locale-aware
 * number formatting stays the caller's job (the homepage's counts are
 * plain integers).
 */
export type MessageVars = Readonly<Record<string, string | number>>;

/** Dictionary shape — every locale must satisfy this. */
export type Messages = Record<MessageKey, string>;

/** Localstorage key for persisted preference. */
export const LOCALE_STORAGE_KEY = 'jubuddy:i18n:locale:v1';

/** URL query-string key for shareable language-specific links. */
export const LOCALE_QUERY_KEY = 'lang';

/** Cookie name — same token ichina / yok-zhang write (`locale=<code>`). */
export const LOCALE_COOKIE_KEY = 'locale';

export function isLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as ReadonlyArray<string>).includes(value);
}
